import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import './Appearance.css';

/* ─────────────────────────────────────────────────────────────
   THEME DEFINITIONS
   All themes live here. Add new unlockable themes to this array.
   unlockElo: 0 means always available.
   ───────────────────────────────────────────────────────────── */
const THEMES = [
  {
    id:          'dark',
    name:        'Dark',
    description: 'The default. Clean and sharp.',
    unlockElo:   0,
  },
  {
    id:          'midnight',
    name:        'Midnight',
    description: 'Neon blue aurora with a star field.',
    unlockElo:   0,
  },
  {
    id:          'flower',
    name:        'Flower',
    description: 'Warm cream and soft pink florals.',
    unlockElo:   0,
  },
  {
    id:          'light',
    name:        'Light',
    description: 'Cream white, bright and airy.',
    unlockElo:   0,
  },
  {
    id:          'hacker',
    name:        'Hacker',
    description: 'Neon green terminal aesthetic.',
    unlockElo:   1000,
  },
];

/* Colour swatches — hardcoded hex so the preview works in any active theme */
const THEME_SWATCHES = {
  dark:     { bg: '#0e0e10', surface: '#1c1c22', accent: '#D4601C' },
  midnight: { bg: '#080b14', surface: '#0e1630', accent: '#22d3ee' },
  flower:   { bg: '#fdf0dc', surface: '#f0d0e0', accent: '#c054a0' },
  light:    { bg: '#faf7f0', surface: '#eae4d4', accent: '#E86C1F' },
  hacker:   { bg: '#080b08', surface: '#111811', accent: '#00ff41' },
};

const DEFAULT_CYCLE = ['dark', 'midnight', 'flower', 'light'];

/* ─────────────────────────────────────────────────────────────
   LOCK ICON
   ───────────────────────────────────────────────────────────── */
function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function UnlockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   APPEARANCE PAGE
   ───────────────────────────────────────────────────────────── */
function Appearance({ theme, setTheme }) {
  const { user }   = useAuth();
  const navigate   = useNavigate();

  // Player's ELO — used to gate locked themes
  const [eloRating, setEloRating] = useState(0);
  const [loading,   setLoading]   = useState(true);

  // Advanced Mode — controls whether the custom cycle is used
  const [advancedOn, setAdvancedOn] = useState(
    () => localStorage.getItem('ranktt-advanced-themes') === 'true'
  );

  // Custom cycle — which themes are included in navbar cycling
  const [cycle, setCycle] = useState(() => {
    const raw = localStorage.getItem('ranktt-theme-cycle');
    try { return raw ? JSON.parse(raw) : DEFAULT_CYCLE; } catch { return DEFAULT_CYCLE; }
  });

  // ── Load ELO on mount ─────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('elo_rating')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        setEloRating(data?.elo_rating ?? 0);
        setLoading(false);
      });
  }, [user]);

  // ── Persist Advanced Mode toggle ──────────────────────────
  function toggleAdvancedMode() {
    const next = !advancedOn;
    setAdvancedOn(next);
    if (next) {
      localStorage.setItem('ranktt-advanced-themes', 'true');
    } else {
      localStorage.removeItem('ranktt-advanced-themes');
    }
  }

  // ── Toggle a theme in/out of the cycle ────────────────────
  function toggleCycleTheme(id) {
    // Don't allow removing the last theme from the cycle
    if (cycle.includes(id) && cycle.length === 1) return;

    const next = cycle.includes(id)
      ? cycle.filter(t => t !== id)
      : [...cycle, id];

    setCycle(next);
    localStorage.setItem('ranktt-theme-cycle', JSON.stringify(next));
  }

  // ── Revert to normal ──────────────────────────────────────
  function revertToNormal() {
    setCycle(DEFAULT_CYCLE);
    setAdvancedOn(false);
    localStorage.setItem('ranktt-theme-cycle', JSON.stringify(DEFAULT_CYCLE));
    localStorage.removeItem('ranktt-advanced-themes');
    // Exit hacker mode when reverting — go back to dark
    if (theme === 'hacker') setTheme('dark');
  }

  return (
    <div className="appearance-page">
      <div className="appearance-inner">

        {/* Back button */}
        <button
          type="button"
          className="appearance-back-btn"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <h1 className="appearance-title">Appearance</h1>
        <p className="appearance-subtitle">
          Choose your theme. Unlock new ones as you climb the leaderboard.
        </p>

        {/* ── Default themes ──────────────────────────────── */}
        <div className="appearance-section-label">Default</div>
        <div className="appearance-grid">
          {THEMES.filter(t => t.unlockElo === 0).map(t => {
            const swatches  = THEME_SWATCHES[t.id];
            const isActive  = theme === t.id;
            const inCycle   = cycle.includes(t.id);

            return (
              <button
                key={t.id}
                type="button"
                className={`appearance-card${isActive ? ' active' : ''}`}
                onClick={() => setTheme(t.id)}
                aria-pressed={isActive}
              >
                {/* Advanced mode checkbox */}
                {advancedOn && (
                  <div
                    className={`appearance-cycle-check ${inCycle ? 'checked' : ''}`}
                    onClick={e => {
                      e.stopPropagation();
                      toggleCycleTheme(t.id);
                    }}
                    role="checkbox"
                    aria-checked={inCycle}
                    aria-label={`Include ${t.name} in navbar cycle`}
                  >
                    {inCycle && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                )}

                {/* Swatch row */}
                <div className="appearance-swatch-row">
                  <span className="appearance-swatch" style={{ background: swatches.bg }} />
                  <span className="appearance-swatch" style={{ background: swatches.surface }} />
                  <span className="appearance-swatch" style={{ background: swatches.accent }} />
                </div>

                {/* Name + description */}
                <div className="appearance-card-body">
                  <div className="appearance-card-name-row">
                    <span className="appearance-card-name">{t.name}</span>
                    {isActive && <span className="appearance-active-badge">Active</span>}
                  </div>
                  <p className="appearance-card-desc">{t.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Exclusive themes ─────────────────────────────── */}
        <div className="appearance-exclusive-header">
          <div className="appearance-section-divider" />
          <div className="appearance-exclusive-label-wrap">
            <span className="appearance-section-label">Exclusive</span>
            <span className="appearance-exclusive-sub">Unlock by reaching ELO milestones</span>
          </div>
        </div>
        <div className="appearance-grid">
          {THEMES.filter(t => t.unlockElo > 0).map(t => {
            const swatches = THEME_SWATCHES[t.id];
            const isActive = theme === t.id;
            const isLocked = eloRating < t.unlockElo;
            const inCycle  = cycle.includes(t.id);

            return (
              <button
                key={t.id}
                type="button"
                className={`appearance-card${isActive ? ' active' : ''}${isLocked ? ' locked' : ''}`}
                onClick={isLocked ? undefined : () => setTheme(t.id)}
                disabled={isLocked}
                aria-pressed={isActive}
              >
                {/* Advanced mode checkbox */}
                {advancedOn && !isLocked && (
                  <div
                    className={`appearance-cycle-check ${inCycle ? 'checked' : ''}`}
                    onClick={e => { e.stopPropagation(); toggleCycleTheme(t.id); }}
                    role="checkbox"
                    aria-checked={inCycle}
                    aria-label={`Include ${t.name} in navbar cycle`}
                  >
                    {inCycle && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                )}

                <div className="appearance-swatch-row">
                  <span className="appearance-swatch" style={{ background: swatches.bg }} />
                  <span className="appearance-swatch" style={{ background: swatches.surface }} />
                  <span className="appearance-swatch" style={{ background: swatches.accent }} />
                </div>

                <div className="appearance-card-body">
                  <div className="appearance-card-name-row">
                    <span className="appearance-card-name">{t.name}</span>
                    {isActive && <span className="appearance-active-badge">Active</span>}
                    {t.unlockElo > 0 && (
                      <span className={`appearance-lock-badge${isLocked ? '' : ' unlocked'}`}>
                        {isLocked ? <LockIcon /> : <UnlockIcon />} Requires {t.unlockElo} ELO
                      </span>
                    )}
                  </div>
                  <p className="appearance-card-desc">{t.description}</p>
                </div>
              </button>
            );
          })}
        </div>
        {/* ── Advanced Mode section ────────────────────────── */}
        <section className="appearance-advanced-section">
          <div className="appearance-advanced-header">
            <div>
              <p className="appearance-advanced-title">Advanced Mode</p>
              <p className="appearance-advanced-sub">
                {advancedOn
                  ? 'Select which themes appear in the navbar cycle.'
                  : 'Customise which themes the navbar button cycles through.'}
              </p>
            </div>
            <button
              type="button"
              className={`appearance-adv-toggle ${advancedOn ? 'on' : ''}`}
              onClick={toggleAdvancedMode}
              aria-label={advancedOn ? 'Disable advanced mode' : 'Enable advanced mode'}
            >
              <span className="appearance-adv-knob" />
            </button>
          </div>

          {advancedOn && (
            <p className="appearance-cycle-hint">
              Currently cycling: {cycle.map(id => THEMES.find(t => t.id === id)?.name).filter(Boolean).join(' → ')}
            </p>
          )}

          <button
            type="button"
            className="appearance-revert-btn"
            onClick={revertToNormal}
          >
            Revert to normal
          </button>
        </section>

      </div>
    </div>
  );
}

export default Appearance;
